import { FormattedMessage } from "react-intl";
import { Grid, useMediaQuery } from "@mui/material";
import { Add, Create, Delete, Done } from "@mui/icons-material";

import { emptyStateString } from "@dzambalaorg/draft-js-utils";
import { Breadcrumbs, PageHeader, ProgressOverlay } from "@dzambalaorg/mui-page-layout";
import { SelectInput } from "@dzambalaorg/mui-inputs-core";
import { DeleteDialog } from "@dzambalaorg/mui-dialog-delete";
import { SearchWithFilters } from "@dzambalaorg/mui-form-search";
import { emptyPrice } from "@dzambalaorg/mui-inputs-asset";
import { useDialogDelete, useDialogForm, useDialogView, useFetchQuery } from "@dzambalaorg/react-hooks";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import { ListAction, ListActions, StyledListItem, StyledListWrapper, StyledPagination } from "@framework/styled";
import { IPredictionQuestion, PredictionQuestionStatus } from "@framework/types";
import {
  PredictionQuestionReleaseButton,
  PredictionQuestionResolveButton,
  PredictionQuestionStartButton,
} from "@framework/shared";
import { Button, List } from "@dzambalaorg/ui-kit";

import { PredictionResultDialog } from "./result";
import { PredictionQuestionDialogForm } from "./form";

const PREDICTION_QUESTIONS_URL = "/prediction/questions";
const PREDICTION_QUESTIONS_DIALOG_KEY = "predictionQuestions";
const emptyValues = {
  title: "",
  description: emptyStateString,
  price: emptyPrice,
  maxVotes: 0,
};

export const PredictionQuestions = () => {
  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { isLoading, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount, refetch } = useFetchQuery<
    IPaginationResult<IPredictionQuestion>
  >(PREDICTION_QUESTIONS_URL, { enableInfiniteScroll: isMd });

  const { handleForm, mode, selectedItem, formDialogUniqueKey } = useDialogForm<IPredictionQuestion>();
  const { viewItem, handleView, viewDialogUniqueKey, closeViewDialog, viewMode } = useDialogView<IPredictionQuestion>();
  const { handleDelete } = useDialogDelete<IPredictionQuestion>();

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "prediction", "prediction.questions"]} />

      <PageHeader message="pages.prediction.questions.title">
        <Button
          intent="secondary"
          startIcon={<Add />}
          onClick={handleForm(PREDICTION_QUESTIONS_DIALOG_KEY, emptyValues, "create")}
          data-testid="QuestionCreateButton"
        >
          <FormattedMessage id="form.buttons.create" />
        </Button>
      </PageHeader>

      <SearchWithFilters
        initialValues={{
          query: "",
          questionStatus: [],
        }}
      >
        <Grid container columnSpacing={2} alignItems="flex-end">
          <Grid size={{ xs: 12 }}>
            <SelectInput multiple name="questionStatus" options={PredictionQuestionStatus} />
          </Grid>
        </Grid>
      </SearchWithFilters>

      <ProgressOverlay isLoading={isLoading}>
        <StyledListWrapper count={allRows.length} isLoading={isLoading}>
          {allRows.map(question => (
            <StyledListItem key={question.id}>
              <List.ItemText sx={{ width: 0.6 }}>{question.title}</List.ItemText>
              <ListActions dataTestId="QuestionMenuButton">
                <ListAction
                  onClick={handleForm(PREDICTION_QUESTIONS_DIALOG_KEY, question, "edit")}
                  message="form.buttons.edit"
                  dataTestId="QuestionEditButton"
                  icon={Create}
                />
                <ListAction
                  onClick={handleView(PREDICTION_QUESTIONS_DIALOG_KEY, question, "view")}
                  message="form.buttons.resolve"
                  icon={Done}
                />
                <PredictionQuestionStartButton question={question} />
                <PredictionQuestionResolveButton question={question} />
                <PredictionQuestionReleaseButton question={question} />
                <ListAction
                  onClick={handleDelete(question)}
                  message="form.buttons.delete"
                  dataTestId="QuestionDeleteButton"
                  icon={Delete}
                  disabled={question.questionStatus === PredictionQuestionStatus.INACTIVE}
                />
              </ListActions>
            </StyledListItem>
          ))}
        </StyledListWrapper>
      </ProgressOverlay>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}

      <DeleteDialog url={PREDICTION_QUESTIONS_URL} refetch={refetch} />

      <PredictionQuestionDialogForm
        open={formDialogUniqueKey === `${PREDICTION_QUESTIONS_DIALOG_KEY}.${mode}`}
        mode={mode}
        selectedItem={selectedItem}
        url={PREDICTION_QUESTIONS_URL}
        refetch={refetch}
      />

      {viewItem && (
        <PredictionResultDialog
          onCancel={closeViewDialog}
          onConfirm={closeViewDialog}
          open={viewDialogUniqueKey === `${PREDICTION_QUESTIONS_DIALOG_KEY}.${viewMode}`}
          viewItem={viewItem}
        />
      )}
    </Grid>
  );
};
